<template>
    <component :is="embedded ? 'div' : 'el-dialog'" v-bind="containerProps"
        @update:visible="handleDialogVisible">
        <div class="auto-msku-toolbar">
            <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="fetchTasks">
                刷新
            </el-button>
            <el-select v-model="ownerFilter" size="small" clearable filterable
                placeholder="按创建人筛选" :loading="usersLoading"
                class="auto-msku-user-filter" @change="fetchTasks">
                <el-option v-for="user in userOptions" :key="user.value"
                    :label="user.label" :value="user.value" />
            </el-select>
            <el-input v-model="searchFilters.msku" size="small" clearable placeholder="搜索 MSKU"
                class="auto-msku-search-input" @keyup.enter.native="fetchTasks" @clear="fetchTasks" />
            <el-input v-model="searchFilters.sku" size="small" clearable placeholder="搜索关联 SKU"
                class="auto-msku-search-input" @keyup.enter.native="fetchTasks" @clear="fetchTasks" />
            <el-input v-model="searchFilters.pm" size="small" clearable placeholder="搜索品名"
                class="auto-msku-search-input" @keyup.enter.native="fetchTasks" @clear="fetchTasks" />
            <el-input v-model="searchFilters.parent" size="small" clearable placeholder="搜索父体"
                class="auto-msku-search-input" @keyup.enter.native="fetchTasks" @clear="fetchTasks" />
            <el-button size="small" type="primary" icon="el-icon-search" :loading="loading" @click="fetchTasks">搜索</el-button>
            <el-button size="small" @click="resetSearch">重置</el-button>
            <span class="auto-msku-tip">支持包含匹配，多个条件同时生效。</span>
        </div>
        <div class="auto-msku-toolbar">
            <el-button size="small" type="warning" :loading="bulkLoading.pause"
                :disabled="!canPauseSelected" @click="runSelectedMonitorAction('pause')">
                暂停所选
            </el-button>
            <el-button size="small" type="primary" :loading="bulkLoading.resume"
                :disabled="!canResumeSelected" @click="runSelectedMonitorAction('resume')">
                恢复／重新监听所选
            </el-button>
            <el-button size="small" type="success" plain :loading="bulkLoading.retry_pair"
                :disabled="!canRetryPairSelected" @click="runSelectedMonitorAction('retry_pair')">
                重试配对
            </el-button>
            <el-button size="small" type="danger" plain :loading="bulkLoading.ignore"
                :disabled="!canIgnoreSelected" @click="runSelectedMonitorAction('ignore')">
                忽略所选
            </el-button>
            <el-button size="small" type="danger" :loading="bulkLoading.delete"
                :disabled="!canDeleteSelected" @click="runSelectedMonitorAction('delete')">
                删除所选
            </el-button>
            <span class="auto-msku-tip">
                开启“创建后配对”的 MSKU 会在生成满 30 分钟后查询 ERP。超时后可重新监听；恢复／重新监听均重新计时 12 小时。
                忽略后仍展示且可恢复；删除后不再展示和轮询，不影响 ERP 商品及配对关系。
                配对接口返回成功即标记“已配对”，不等待 local_sku 回传；只有“配对失败待重试”可以点击“重试配对”。
            </span>
        </div>

        <el-table :data="taskList" size="small" border stripe max-height="560"
            v-loading="loading" @selection-change="onSelectionChange">
            <el-table-column type="selection" width="46" :selectable="isSelectable" />
            <el-table-column prop="parent" label="父体" min-width="130" show-overflow-tooltip />
            <el-table-column prop="pm" label="品名" min-width="140" show-overflow-tooltip />
            <el-table-column prop="shop" label="店铺" width="120" />
            <el-table-column prop="username" label="创建人" width="100" show-overflow-tooltip />
            <el-table-column prop="sku" label="关联SKU" min-width="130" show-overflow-tooltip />
            <el-table-column prop="msku" label="MSKU" min-width="230" show-overflow-tooltip />
            <el-table-column label="同步状态" width="125" align="center">
                <template slot-scope="scope">
                    <el-tag size="mini" :type="statusType(scope.row.status)">
                        {{ scope.row.status === 'paired' ? '已配对' : (scope.row.status_msg || scope.row.sync_status || '未监控') }}
                    </el-tag>
                </template>
            </el-table-column>
            <el-table-column prop="fnsku" label="FNSKU" min-width="130" show-overflow-tooltip />
            <el-table-column prop="asin" label="ASIN" width="125" show-overflow-tooltip />
            <el-table-column prop="local_sku" label="local_sku" min-width="130" show-overflow-tooltip />
            <el-table-column prop="created_at" label="创建时间" width="160" />
            <el-table-column prop="deadline_at" label="截止时间" width="160" />
            <el-table-column label="操作" width="250" fixed="right" align="center">
                <template slot-scope="scope">
                    <el-button v-if="isRetryPairable(scope.row)" type="text" size="mini"
                        style="color:#67C23A;" :loading="scope.row._monitorAction === 'retry_pair'"
                        @click="runRowMonitorAction(scope.row, 'retry_pair')">
                        重试配对
                    </el-button>
                    <template v-if="isPauseable(scope.row)">
                        <el-button type="text" size="mini" :loading="scope.row._monitorAction === 'pause'"
                            @click="runRowMonitorAction(scope.row, 'pause')">
                            暂停
                        </el-button>
                    </template>
                    <el-button v-else-if="isResumable(scope.row)" type="text" size="mini"
                        :loading="scope.row._monitorAction === 'resume'"
                        @click="runRowMonitorAction(scope.row, 'resume')">
                        {{ scope.row.status === 'expired' ? '重新监听' : '恢复监控' }}
                    </el-button>
                    <el-button v-if="isIgnorable(scope.row)" type="text" size="mini" style="color:#F56C6C;"
                        :loading="scope.row._monitorAction === 'ignore'"
                        @click="runRowMonitorAction(scope.row, 'ignore')">忽略</el-button>
                    <el-button v-if="isSelectable(scope.row)" type="text" size="mini" style="color:#F56C6C;"
                        :loading="scope.row._monitorAction === 'delete'"
                        @click="runRowMonitorAction(scope.row, 'delete')">删除</el-button>
                </template>
            </el-table-column>
        </el-table>
        <div v-if="!loading && !taskList.length" class="auto-msku-empty">暂无符合条件的 Auto MSKU 记录</div>
    </component>
</template>

<script>
export default {
    name: 'AutoMskuTaskDialog',
    props: {
        visible: { type: Boolean, default: false },
        // Auto工作台内嵌时不再创建第二层 el-dialog。
        embedded: { type: Boolean, default: false },
        // 内嵌模式只在当前分页激活时加载，避免隐藏页签提前请求。
        active: { type: Boolean, default: true },
    },
    data() {
        return {
            taskList: [],
            selectedRows: [],
            loading: false,
            usersLoading: false,
            userOptions: [],
            ownerFilter: '',
            searchFilters: { msku: '', sku: '', pm: '', parent: '' },
            taskRequestId: 0,
            bulkLoading: { pause: false, resume: false, retry_pair: false, ignore: false, delete: false },
        };
    },
    computed: {
        containerProps() {
            if (this.embedded) return {};
            return {
                title: 'Auto MSKU记录',
                visible: this.visible,
                width: '94%',
                closeOnClickModal: false,
                appendToBody: true,
            };
        },
        visibleSync: {
            get() { return this.visible; },
            set(value) { this.$emit('update:visible', value); },
        },
        canPauseSelected() {
            return this.selectedRows.some(row => this.isPauseable(row));
        },
        canResumeSelected() {
            return this.selectedRows.some(row => this.isResumable(row));
        },
        canRetryPairSelected() {
            return this.selectedRows.some(row => this.isRetryPairable(row));
        },
        canIgnoreSelected() {
            return this.selectedRows.some(row => this.isIgnorable(row));
        },
        canDeleteSelected() {
            return this.selectedRows.some(row => this.isSelectable(row));
        },
    },
    watch: {
        visible(value) {
            if (value && (!this.embedded || this.active)) {
                this.fetchTasks();
            }
        },
        active(value) {
            if (!this.embedded) return;
            if (value && this.visible) {
                this.fetchTasks();
            }
        },
    },
    mounted() {
        if (this.visible && (!this.embedded || this.active)) {
            this.fetchTasks();
        }
    },
    methods: {
        handleDialogVisible(value) {
            if (!this.embedded) this.visibleSync = value;
        },
        statusType(status) {
            const types = {
                generated: 'info', created_only: 'info', waiting_product: 'warning', waiting_fnsku: 'warning',
                waiting_pair: 'danger', waiting_local_sku: 'warning', paired: 'success',
                expired: 'danger', manual_review: 'danger', paused: 'info', ignored: 'info',
            };
            return types[status] || 'info';
        },
        isSelectable(row) {
            return !!row && !!row.id && !['paired', 'deleted'].includes(row.status);
        },
        isPauseable(row) {
            return this.isSelectable(row)
                && ['waiting_product', 'waiting_fnsku', 'waiting_pair', 'waiting_local_sku'].includes(row.status);
        },
        isResumable(row) {
            return this.isSelectable(row)
                && ['paused', 'ignored', 'generated', 'expired'].includes(row.status);
        },
        isRetryPairable(row) {
            return !!row && row.status === 'waiting_pair';
        },
        isIgnorable(row) {
            return this.isPauseable(row) || (this.isSelectable(row) && ['paused', 'generated'].includes(row.status));
        },
        onSelectionChange(rows) {
            this.selectedRows = rows || [];
        },
        fetchUsers() {
            this.usersLoading = true;
            // 只展示已经创建过 Auto MSKU 记录的去重创建人，不读取全量账号列表。
            this.$axios.get('/vue/msku-generation/task-owners')
                .then(res => {
                    if (res.code === 200) {
                        this.userOptions = res.data || [];
                    } else {
                        this.$message.error(res.message || '创建人列表获取失败');
                    }
                })
                .catch(() => this.$message.error('创建人列表获取失败'))
                .finally(() => { this.usersLoading = false; });
        },
        resetSearch() {
            this.ownerFilter = '';
            this.searchFilters = { msku: '', sku: '', pm: '', parent: '' };
            return this.fetchTasks();
        },
        fetchTasks() {
            const requestId = ++this.taskRequestId;
            this.loading = true;
            this.selectedRows = [];
            this.fetchUsers();
            const params = { limit: 300 };
            if (this.ownerFilter) params.owner_account = this.ownerFilter;
            Object.keys(this.searchFilters).forEach(field => {
                const value = this.searchFilters[field].trim();
                if (value) params[field] = value;
            });
            return this.$axios.get('/vue/msku-generation/tasks', { params })
                .then(res => {
                    if (requestId !== this.taskRequestId) return;
                    if (res.code === 200) {
                        this.taskList = (res.data || []).map(row => Object.assign({ _monitorAction: '' }, row));
                    } else {
                        this.$message.error(res.message || 'Auto MSKU 记录查询失败');
                    }
                })
                .catch(() => {
                    if (requestId === this.taskRequestId) this.$message.error('Auto MSKU 记录查询失败');
                })
                .finally(() => {
                    if (requestId === this.taskRequestId) this.loading = false;
                });
        },
        runRowMonitorAction(row, action) {
            if (!this.isSelectable(row)) return;
            if (action === 'delete') {
                this.confirmDelete([row.id], row);
                return;
            }
            if (action === 'retry_pair') {
                this.$confirm(
                    '将立即调用 ERP 配对方法。请先确认上次配对确实失败，且 ERP 中没有有效的旧配对关系，是否继续？',
                    '确认重试配对',
                    { confirmButtonText: '确认重试', cancelButtonText: '取消', type: 'warning' }
                ).then(() => this.runMonitorAction(action, [row.id], row)).catch(() => { });
                return;
            }
            if (action === 'ignore') {
                this.$confirm(
                    '忽略后停止轮询和超时通知，记录仍展示且可恢复监控，是否继续？',
                    '确认忽略监控',
                    { confirmButtonText: '确认忽略', cancelButtonText: '取消', type: 'warning' }
                ).then(() => this.runMonitorAction(action, [row.id], row)).catch(() => { });
                return;
            }
            this.runMonitorAction(action, [row.id], row);
        },
        runSelectedMonitorAction(action) {
            if (action === 'delete') {
                const ids = this.selectedRows.filter(row => this.isSelectable(row)).map(row => row.id);
                if (ids.length) this.confirmDelete(ids);
                return;
            }
            const canRun = action === 'pause' ? this.canPauseSelected
                : action === 'resume' ? this.canResumeSelected
                    : action === 'retry_pair' ? this.canRetryPairSelected : this.canIgnoreSelected;
            if (!canRun) return;
            const rows = this.selectedRows.filter(row => action === 'pause'
                ? this.isPauseable(row)
                : action === 'resume' ? this.isResumable(row)
                    : action === 'retry_pair' ? this.isRetryPairable(row) : this.isIgnorable(row));
            if (!rows.length) return;
            const ids = rows.map(row => row.id);
            if (action === 'retry_pair') {
                this.$confirm(
                    '将立即为选中的 ' + ids.length + ' 条记录调用 ERP 配对方法。请先确认这些记录配对确实失败，且 ERP 中没有有效的旧配对关系，是否继续？',
                    '确认重试配对',
                    { confirmButtonText: '确认重试', cancelButtonText: '取消', type: 'warning' }
                ).then(() => this.runMonitorAction(action, ids)).catch(() => { });
                return;
            }
            if (action === 'ignore') {
                this.$confirm(
                    '忽略后选中的 ' + ids.length + ' 条 MSKU 停止轮询和超时通知，记录仍展示且可恢复，是否继续？',
                    '确认忽略监控',
                    { confirmButtonText: '确认忽略', cancelButtonText: '取消', type: 'warning' }
                ).then(() => this.runMonitorAction(action, ids)).catch(() => { });
                return;
            }
            this.runMonitorAction(action, ids);
        },
        confirmDelete(ids, row) {
            this.$confirm(
                '删除这 ' + ids.length + ' 条 MSKU 记录后，将从工作台隐藏并停止后续轮询，无法在工作台恢复。不会删除 ERP 商品或解除配对；已经发出的 ERP 请求可能仍会完成。是否继续？',
                '确认删除记录',
                { confirmButtonText: '确认删除', cancelButtonText: '取消', type: 'warning' }
            ).then(() => this.runMonitorAction('delete', ids, row)).catch(() => { });
        },
        runMonitorAction(action, ids, row) {
            const endpoints = {
                pause: '/vue/msku-generation/monitor/pause',
                resume: '/vue/msku-generation/monitor/resume',
                retry_pair: '/vue/msku-generation/monitor/retry-pair',
                ignore: '/vue/msku-generation/monitor/ignore',
                delete: '/vue/msku-generation/monitor/delete',
            };
            if (!endpoints[action] || !ids.length) return;
            if (row) this.$set(row, '_monitorAction', action);
            else this.$set(this.bulkLoading, action, true);
            const labels = {
                pause: '暂停监控', resume: '恢复／重新监听', retry_pair: '重试配对', ignore: '忽略监控', delete: '删除记录',
            };
            this.$axios.post(endpoints[action], { task_ids: ids })
                .then(res => {
                    if (res.code === 200) {
                        if (action === 'retry_pair' && res.data && res.data.outcomes) {
                            // 新逻辑配对接口成功即完成；兼容旧后端返回的 waiting_local_sku 统计。
                            const paired = res.data.outcomes.paired
                                || res.data.outcomes.waiting_local_sku || 0;
                            const failed = res.data.outcomes.waiting_pair || 0;
                            if (failed) {
                                this.$message.warning(
                                    '已执行重试：' + paired + ' 条配对成功，' + failed + ' 条仍然失败'
                                );
                            } else {
                                this.$message.success(
                                    '配对方法调用成功，共 ' + paired + ' 条已配对'
                                );
                            }
                        } else {
                            this.$message.success(labels[action] + '成功');
                        }
                        this.fetchTasks();
                    } else {
                        this.$message.error(res.message || labels[action] + '失败');
                    }
                })
                .catch(() => this.$message.error(labels[action] + '失败'))
                .finally(() => {
                    if (row) this.$set(row, '_monitorAction', '');
                    else this.$set(this.bulkLoading, action, false);
                });
        },
    },
};
</script>

<style scoped>
.auto-msku-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.auto-msku-user-filter {
    width: 180px;
}

.auto-msku-search-input {
    width: 190px;
}

.auto-msku-tip {
    font-size: 12px;
    color: #909399;
}

.auto-msku-empty {
    padding: 18px 0;
    text-align: center;
    color: #909399;
    font-size: 13px;
}
</style>
